import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '../apiClient';

const key = (storeId, asin) => `${storeId}:${asin}`;

/**
 * Owns the current user's row marks: a Set keyed "storeId:asin", plus toggle/clearAll.
 *
 * - Fetches once on mount from GET /marks.
 * - toggleMark(storeId, asin, next) does an optimistic update and rolls back on failure.
 * - clearAll() hits DELETE /marks and empties the Set on success.
 * - isMarked(storeId, asin) is O(1).
 */
export function useMarks() {
    const [marks, setMarks] = useState(() => new Set());
    const [loaded, setLoaded] = useState(false);
    const errorRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await api.marks();
                if (cancelled) return;
                setMarks(new Set(data.map((m) => key(m.store_id, m.asin))));
            } catch (_) {
                // Best-effort load. Marks just stay empty; user can still mark.
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const isMarked = useCallback(
        (storeId, asin) => marks.has(key(storeId, asin)),
        [marks]
    );

    const toggleMark = useCallback(async (storeId, asin, next) => {
        const k = key(storeId, asin);
        // Optimistic update
        setMarks((prev) => {
            const copy = new Set(prev);
            if (next) copy.add(k); else copy.delete(k);
            return copy;
        });
        try {
            await (next ? api.mark(storeId, asin) : api.unmark(storeId, asin));
        } catch (e) {
            // Roll back
            setMarks((prev) => {
                const copy = new Set(prev);
                if (next) copy.delete(k); else copy.add(k);
                return copy;
            });
            errorRef.current = e;
        }
    }, []);

    const clearAll = useCallback(async () => {
        const prev = marks;
        setMarks(new Set()); // optimistic
        try {
            await api.clearMarks();
        } catch (e) {
            setMarks(prev); // rollback to whatever was there
            errorRef.current = e;
        }
    }, [marks]);

    return { isMarked, toggleMark, clearAll, markCount: marks.size, loaded };
}
